<template>
  <div
    class="expandable-content"
    :class="{ collapsed: !expanded, expanded }"
    :style="{
      '-webkit-line-clamp': !expanded ? lines : undefined
    }"
    ref="expandableContent"
  >
    <slot />
  </div>
</template>

<script lang="ts" setup>
const props = defineProps<{
  lines: number
  expanded?: boolean
}>()

const emit = defineEmits<{
  expandable: [value: boolean]
}>()

const expandableContent = ref<InstanceType<typeof HTMLDivElement>>()
const observer = ref<MutationObserver>()

const resetLineClamp = () => {
  const computedStyles = getComputedStyle(expandableContent.value!)

  expandableContent.value?.style.setProperty('-webkit-line-clamp', String(props.lines))

  const expandedHeight = expandableContent.value?.scrollHeight ?? 0

  expandableContent.value?.style.setProperty('--expanded-height', `${expandedHeight}px`)

  const collapsedHeight = Number(computedStyles.getPropertyValue('line-height').replace(/[^\d]/g, '')) * props.lines
  const isExpandable = expandedHeight > collapsedHeight

  expandableContent.value?.style.setProperty(
    '--collapsed-height',
    `${isExpandable ? collapsedHeight : expandedHeight}px`
  )

  if (props.expanded || !isExpandable) expandableContent.value?.style.removeProperty('-webkit-line-clamp')

  emit('expandable', isExpandable)
}

onMounted(() => {
  resetLineClamp()

  observer.value = new MutationObserver(resetLineClamp)
  observer.value.observe(expandableContent.value!, {
    childList: true,
    subtree: true,
    characterData: true
  })
})

onBeforeUnmount(() => {
  observer.value?.disconnect()
})
</script>

<style lang="scss" scoped>
.expandable-content {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  transition:
    min-height 1s ease-in-out,
    max-height 1s ease-in-out;
  min-height: var(--collapsed-height);
  max-height: var(--collapsed-height);

  &.expanded {
    max-height: var(--expanded-height);
    min-height: var(--expanded-height);
  }
}
</style>
